/**
 * analytics.ts — #umami self-hosted analytics — event taxonomy + thin wrapper.
 *
 * Replaces the prior PostHog wrapper (2026-06-02). Same export surface so
 * call-sites do not change:
 *
 *   - `initAnalytics(websiteId, host)` — call once after runtime-config resolves.
 *   - `track(event, props?)` — fire a named event.
 *   - `identify(sub, traits?)` — set the current distinct id (cognito_sub).
 *   - `resetIdentity()` — clear distinct id on logout.
 *   - `trackDailyActive()` — once-per-UTC-day debounced `daily_active`.
 *
 * Wire format: HTTPS POST to `${host}/api/send` with the Umami payload shape
 * documented at https://umami.is/docs/sending-stats. No client SDK is loaded —
 * we send events via plain `fetch()` so there is no extra JS in the bundle
 * and no script-src CSP entry is needed for a tracker file.
 *
 * Identity:
 *   - Umami is privacy-by-default and does NOT have a first-class "identify"
 *     API like PostHog. The closest analogue is sending a custom event with
 *     the distinct id in `data.distinct_id`. We stash the current id in
 *     module state and attach it to every subsequent track() payload, so the
 *     existing call-sites that called `identify(sub)` keep their semantic
 *     meaning: "from now on, all events should be attributed to this user."
 *
 * Privacy / signal-to-noise (founder rule, 2026-06-02):
 *   - NO autocapture, NO automatic page-view tracking.
 *   - Only the named taxonomy below fires.
 *
 * Event taxonomy (all events in the `2hives_` namespace) — UNCHANGED:
 *
 *   Funnel — acquisition & activation
 *     landing_view            — user lands on /  (once per session)
 *     signup_start            — user reaches /signup
 *     signup_complete         — Cognito SignUp API succeeds
 *
 *   Demo funnel
 *     demo_start              — user reaches /try
 *     demo_complete           — user clicks "Sign up" CTA from demo page
 *
 *   Checkout / billing
 *     checkout_start          — Stripe checkout redirect initiated
 *     checkout_success        — /upgrade-success webhook confirmed (tier flipped)
 *     subscription_canceled   — subscription cancel action fired (future billing portal)
 *
 *   Agent interaction
 *     agent_spawn             — create_agent MCP call acknowledged
 *     first_message           — user sends their first chat message in a hive
 *     daily_active            — user is active (any API call); debounced to once/day
 *
 *   Support
 *     support_ticket_created  — SupportChatOverlay POST /admin/support/chat succeeds
 */

// ── Types ──────────────────────────────────────────────────────────────────────

export type AnalyticsEvent =
  | "landing_view"
  | "signup_start"
  | "signup_complete"
  | "demo_start"
  | "demo_complete"
  | "checkout_start"
  | "checkout_success"
  | "subscription_canceled"
  | "agent_spawn"
  | "first_message"
  | "daily_active"
  | "support_ticket_created";

export interface AnalyticsProperties {
  [key: string]: string | number | boolean | null | undefined;
}

// ── State ──────────────────────────────────────────────────────────────────────

let _ready = false;
let _websiteId = "";
let _host = "";
let _distinctId: string | null = null;
/** Identity traits captured from identify(); attached to every track payload. */
let _traits: AnalyticsProperties = {};

// ── Init ───────────────────────────────────────────────────────────────────────

/**
 * Call once after runtime-config has resolved (in ConfigProvider).
 * No-ops silently if `websiteId` is empty — safe to call unconditionally.
 *
 * Backward-compatible signature: the first argument used to be the PostHog
 * project key; it is now the Umami website UUID. The second argument is the
 * Umami host (e.g. `https://umami.2hives.ai`).
 */
export function initAnalytics(websiteId: string, host: string): void {
  if (!websiteId) return;   // no website id → fully disabled; track() no-ops
  if (_ready) return;
  _websiteId = websiteId;
  _host = (host || "").replace(/\/+$/, "");   // strip trailing slash
  _ready = !!_host;
}

// ── Internal: send to Umami ────────────────────────────────────────────────────

/**
 * POST a single event to `${host}/api/send`. Best-effort + non-blocking;
 * uses `keepalive: true` so events fired during pagehide are still flushed.
 * Never throws — analytics failure must NOT break the product.
 */
function _send(name: string, data: AnalyticsProperties): void {
  if (!_ready) return;
  try {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const referrer = typeof document !== "undefined" ? document.referrer : "";
    const language =
      typeof navigator !== "undefined" && navigator.language ? navigator.language : "en";
    const screen =
      typeof window !== "undefined" && window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : undefined;

    // Merge identity traits into the per-event data payload.
    const payloadData: AnalyticsProperties = { ..._traits, ...data };
    if (_distinctId) payloadData.distinct_id = _distinctId;

    const body = {
      type: "event",
      payload: {
        website:  _websiteId,
        hostname: typeof window !== "undefined" ? window.location.hostname : "",
        screen,
        language,
        title:    typeof document !== "undefined" ? document.title : "",
        url,
        referrer,
        name,
        data:     payloadData,
      },
    };

    // No Authorization header — /api/send is intentionally anonymous + CORS-permissive.
    void fetch(`${_host}/api/send`, {
      method:      "POST",
      headers:     { "Content-Type": "application/json" },
      body:        JSON.stringify(body),
      credentials: "omit",
      keepalive:   true,
    }).catch(() => {
      /* swallow — never let analytics break the product */
    });
  } catch {
    /* swallow — never let analytics break the product */
  }
}

// ── Track ──────────────────────────────────────────────────────────────────────

/**
 * Fire a named event in the 2hives taxonomy.
 * No-op if analytics not initialised (missing website id).
 */
export function track(event: AnalyticsEvent, properties?: AnalyticsProperties): void {
  _send(event, properties ?? {});
}

// ── Identify ───────────────────────────────────────────────────────────────────

/**
 * Identify the current user. Umami has no first-class identify endpoint, so
 * we stash `sub` + traits in module state and attach them to every subsequent
 * `track()` payload as `data.distinct_id` + flattened traits.
 *
 * @param sub  cognito_sub — stable person ID even across email changes.
 * @param props  Optional enrichment (email, tier, etc).
 */
export function identify(sub: string, props?: AnalyticsProperties): void {
  if (!_ready) return;
  _distinctId = sub || null;
  _traits = props ? { ...props } : {};
}

/**
 * Reset identity on logout. Subsequent events become anonymous.
 */
export function resetIdentity(): void {
  _distinctId = null;
  _traits = {};
}

// ── daily_active debounce ──────────────────────────────────────────────────────

const DAU_KEY = "2hives_dau_date";

/**
 * Fire `daily_active` once per calendar day (UTC) per browser.
 * Safe to call on every API call — cheap localStorage check.
 */
export function trackDailyActive(): void {
  if (!_ready) return;
  const today = new Date().toISOString().slice(0, 10);
  try {
    if (typeof localStorage === "undefined") return;
    if (localStorage.getItem(DAU_KEY) === today) return;
    localStorage.setItem(DAU_KEY, today);
  } catch {
    return;
  }
  track("daily_active");
}

// ── Test hooks ─────────────────────────────────────────────────────────────────

/**
 * Test-only: reset module state between specs. Not exported in production
 * call-sites; lives here so tests don't need to fiddle with module reloading.
 */
export function __resetForTests(): void {
  _ready = false;
  _websiteId = "";
  _host = "";
  _distinctId = null;
  _traits = {};
}
